import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import { 
  FaBuilding, 
  FaUsers, 
  FaMobile,
  FaComments,
  FaCreditCard,
  FaChartLine,
  FaCloud,
  FaShieldAlt,
  FaHeart,
  FaVideo,
  FaChrome
} from "react-icons/fa";

function FeaturedProjects() {
  const [activeFilter, setActiveFilter] = useState("All");

  const projects = [
    {
      id: 1,
      title: "ClickFortify Ad Fraud Protection Platform",
      description: "Architected and developed comprehensive ad fraud protection system that automatically blocks invalid traffic from Google Ads and Facebook Ads in real-time. The platform combines advanced bot detection, competitor click monitoring, and machine learning algorithms to protect ad budgets from fraudulent activities.",
      category: "Security",
      icon: <FaShieldAlt />,
      keyFeatures: [
        "Real-time bot detection and blocking system with ML algorithms",
        "Competitor click protection and automated monitoring",
        "VPN and data center traffic filtering",
        "Behavioral analysis and pattern recognition",
        "Multi-platform support (Google Ads, Facebook Ads)",
        "Comprehensive fraud reporting and analytics dashboard"
      ],
      technologies: ["Node.js Express", "Next.js", "MongoDB", "Multi-tenant Architecture", "BigQuery", "GCP", "TypeScript", "Docker", "Redis", "Apache Kafka", "Machine Learning"],
      impact: "Protected $100M+ in ad spend, 40% reduction in invalid clicks, 99.9% fraud detection accuracy, 90% reduction in competitor click fraud"
    },
    {
      id: 2,
      title: "Core Direction Wellness Platform",
      description: "Developed comprehensive wellness technology platform that inspires global health and happiness through corporate wellness solutions, community engagement, and mobile health coaching. The platform serves major organizations including Dubai Sports Council, Expo 2020, and Fortune 500 companies.",
      category: "Enterprise",
      icon: <FaHeart />,
      keyFeatures: [
        "Corporate wellness technology for employee wellbeing",
        "Community engagement platform with social networking",
        "Mobile health coaching app with personalized guidance",
        "Event management and consulting services",
        "Personal best tracking and fitness challenges",
        "Multi-tenant architecture for enterprise clients"
      ],
      technologies: ["Vue.js", "Node.js NestJS", "Multi-tenant Architecture", "Redis Bull", "NATS", "MongoDB", "PostgreSQL", "AWS", "Docker", "WebSockets", "RESTful APIs"],
      impact: "Serving 50+ major organizations globally, 100K+ active users, 40% improvement in employee wellness metrics"
    },
    {
      id: 3,
      title: "Lytics System - PEMRA Media Monitoring",
      description: "Developed comprehensive multimedia content management and analysis system for Pakistan Electronic Media Regulatory Authority (PEMRA). The platform streamlines media content monitoring and regulation through advanced video processing, transcription, and categorization capabilities.",
      category: "Data",
      icon: <FaVideo />,
      keyFeatures: [
        "Live video clipping and content capture",
        "Automated transcription and translation services",
        "Vast library of categorized media clips",
        "Real-time media content monitoring",
        "Advanced search and filtering capabilities",
        "Multi-format media processing and storage"
      ],
      technologies: ["Node.js", "React", "MongoDB", "PostgreSQL", "AWS", "FFmpeg", "WebRTC", "Elasticsearch", "Redis", "Docker", "Kubernetes"],
      impact: "Streamlined media regulation process, 90% faster content analysis, comprehensive broadcasting industry oversight"
    },
    {
      id: 4,
      title: "Prismfly Browser Extension",
      description: "Built sophisticated browser extension for [Prismfly](https://prismfly.com/) to enhance ecommerce optimization and conversion rate optimization (CRO) capabilities. The extension provides real-time analytics, A/B testing tools, and user behavior tracking for DTC brands.",
      category: "Enterprise",
      icon: <FaChrome />,
      keyFeatures: [
        "Injectable script core with vanilla JavaScript",
        "Real-time user behavior tracking and analytics",
        "A/B testing and conversion optimization tools",
        "Ecommerce performance monitoring",
        "Cross-browser compatibility and seamless integration",
        "Advanced data collection and reporting capabilities"
      ],
      technologies: ["Node.js Express", "React", "Vanilla JavaScript", "Chrome Extension API", "Webpack", "TypeScript", "RESTful APIs", "WebSockets"],
      impact: "Enhanced CRO capabilities for 100+ DTC brands, improved conversion rates by 15-35%, streamlined optimization workflows"
    },
    {
      id: 5,
      title: "Enterprise Microservices Platform",
      description: "Architected and implemented a comprehensive microservices platform serving 50,000+ concurrent users with 15+ distributed services, achieving 99.99% uptime and sub-100ms response times.",
      category: "Architecture",
      icon: <FaBuilding />,
      keyFeatures: [
        "50,000+ concurrent users with sub-100ms response times",
        "15+ distributed microservices with event-driven architecture",
        "Multi-region deployment with fault tolerance",
        "Advanced caching with Redis Cluster",
        "Real-time monitoring with ELK Stack",
        "Zero-downtime deployments with blue-green strategy"
      ],
      technologies: ["Java Spring Boot", "Kubernetes", "Docker", "Apache Kafka", "Redis", "AWS", "Prometheus", "Grafana"],
      impact: "300% scalability improvement, 80% faster deployments, 99.99% uptime SLA"
    },
    {
      id: 6,
      title: "Real-Time Data Processing Pipeline",
      description: "Designed and implemented a high-performance real-time data processing pipeline using Apache Kafka and Apache Spark, processing 1M+ events per second with sub-second latency.",
      category: "Data",
      icon: <FaChartLine />,
      keyFeatures: [
        "1M+ events per second processing capacity",
        "Sub-second end-to-end latency",
        "Real-time analytics and insights",
        "Fault-tolerant stream processing",
        "Scalable data ingestion pipeline",
        "Real-time dashboard and monitoring"
      ],
      technologies: ["Apache Kafka", "Apache Spark", "Python", "Redis", "Elasticsearch", "Kibana", "Docker", "Kubernetes"],
      impact: "Real-time insights delivery, 90% faster data processing, 99.9% data accuracy"
    },
    {
      id: 7,
      title: "Multi-Tenant SaaS Platform",
      description: "Built enterprise-grade multi-tenant SaaS platform managing 5000+ isolated tenant databases with advanced resource control and dynamic schema management.",
      category: "Enterprise",
      icon: <FaUsers />,
      keyFeatures: [
        "5000+ isolated tenant databases",
        "Dynamic schema management",
        "Advanced resource isolation",
        "Multi-tenant security",
        "Scalable billing system",
        "Comprehensive tenant analytics"
      ],
      technologies: ["Java Spring Boot", "PostgreSQL", "Redis", "Docker", "Kubernetes", "AWS", "Stripe API"],
      impact: "Zero downtime, 99.9% uptime, 60% cost reduction, 500% scalability improvement"
    },
    {
      id: 8,
      title: "High-Scale Payment Processing System",
      description: "Developed enterprise payment processing system handling $10M+ monthly transactions with advanced fraud detection, multi-currency support, and PCI DSS compliance.",
      category: "Financial",
      icon: <FaCreditCard />,
      keyFeatures: [
        "$10M+ monthly transaction processing",
        "Advanced fraud detection algorithms",
        "Multi-currency support",
        "PCI DSS compliance",
        "Real-time transaction monitoring",
        "Comprehensive audit trails"
      ],
      technologies: ["Java Spring Boot", "Stripe API", "PostgreSQL", "Redis", "Apache Kafka", "AWS", "ELK Stack"],
      impact: "Zero security incidents, 99.99% transaction success rate, 50% fraud reduction"
    },
    {
      id: 9,
      title: "Real-Time Communication Platform",
      description: "Built high-performance real-time communication platform supporting 25,000+ concurrent users with WebSocket implementation, message encryption, and advanced features.",
      category: "Real-time",
      icon: <FaComments />,
      keyFeatures: [
        "25,000+ concurrent users support",
        "99.9% message delivery rate",
        "End-to-end encryption",
        "Real-time notifications",
        "Cross-platform compatibility",
        "Advanced user engagement features"
      ],
      technologies: ["Node.js", "WebSockets", "Redis", "MongoDB", "React.js", "Docker", "AWS", "Socket.io"],
      impact: "40% user engagement improvement, 45% reduction in drop-off rates, 99.9% reliability"
    },
    {
      id: 10,
      title: "Cloud-Native Infrastructure Platform",
      description: "Architected and deployed cloud-native infrastructure platform using Kubernetes, Docker, and AWS services with comprehensive monitoring and auto-scaling capabilities.",
      category: "DevOps",
      icon: <FaCloud />,
      keyFeatures: [
        "Multi-region Kubernetes deployment",
        "Auto-scaling and load balancing",
        "Comprehensive monitoring stack",
        "Infrastructure as Code",
        "Zero-downtime deployments",
        "Disaster recovery automation"
      ],
      technologies: ["Kubernetes", "Docker", "AWS", "Terraform", "Prometheus", "Grafana", "Jenkins", "ArgoCD"],
      impact: "70% infrastructure cost reduction, 90% faster deployments, 99.99% availability"
    },
    {
      id: 11,
      title: "Mobile Banking Application",
      description: "Led development of secure mobile banking application with biometric authentication, real-time transactions, and comprehensive security features.",
      category: "Mobile",
      icon: <FaMobile />,
      keyFeatures: [
        "Biometric authentication",
        "Real-time transaction processing",
        "Advanced security features",
        "Cross-platform compatibility",
        "Offline capabilities",
        "Push notifications"
      ],
      technologies: ["React Native", "Node.js", "PostgreSQL", "Redis", "AWS", "Firebase", "Stripe API"],
      impact: "98% crash-free sessions, 50% faster transaction processing, 99.9% security compliance"
    }
  ];

  const filters = ["All", "Security", "Enterprise", "Data", "Architecture", "Financial", "Real-time", "DevOps", "Mobile"];

  const filteredProjects = activeFilter === "All" 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <Container fluid className="featured-projects-section" id="featured-projects">
      <Particle />
      <Container>
        <h1 className="project-heading">
          Featured <strong className="purple">Projects</strong>
        </h1>
        <p style={{ color: "white", textAlign: "center", marginBottom: "40px", fontSize: "1.1em" }}>
          Showcase of enterprise-scale applications and high-performance systems delivering measurable business impact and technical excellence.
        </p>

        {/* Filter Buttons */}
        <Row style={{ justifyContent: "center", marginBottom: "30px" }}>
          <Col md={12}>
            <div style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "10px"
            }}>
              {filters.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  style={{
                    backgroundColor: activeFilter === filter 
                      ? "#c770f0" 
                      : "rgba(255, 255, 255, 0.1)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "25px",
                    padding: "8px 20px",
                    fontSize: "0.9em",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    margin: "5px"
                  }}
                  onMouseEnter={(e) => {
                    if (activeFilter !== filter) {
                      e.target.style.backgroundColor = "rgba(199, 112, 240, 0.3)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeFilter !== filter) {
                      e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                >
                  {filter}
                </button>
              ))}
            </div>
          </Col>
        </Row>

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {filteredProjects.map((project, index) => (
            <Col md={6} lg={4} className="project-card" key={project.id} style={{ marginBottom: "40px" }}>
              <div 
                className="project-card-view"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "20px",
                  padding: "35px",
                  color: "white",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  height: "100%",
                  opacity: activeFilter === "All" || activeFilter === project.category ? 1 : 0.6,
                  transform: activeFilter === "All" || activeFilter === project.category ? "scale(1.01)" : "scale(1)",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-3px) scale(1.02)";
                  e.target.style.boxShadow = "0 12px 25px rgba(199, 112, 240, 0.2)";
                  e.target.style.borderColor = "rgba(199, 112, 240, 0.3)";
                  e.target.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0) scale(1)";
                  e.target.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.15)";
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)";
                }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "20px"
                }}>
                  <div style={{
                    fontSize: "2em",
                    color: "#c770f0",
                    marginRight: "15px"
                  }}>
                    {project.icon}
                  </div>
                  <span style={{
                    backgroundColor: "rgba(199, 112, 240, 0.2)",
                    color: "#c770f0",
                    padding: "4px 12px",
                    borderRadius: "15px",
                    fontSize: "0.8em",
                    border: "1px solid rgba(199, 112, 240, 0.3)"
                  }}>
                    {project.category}
                  </span>
                </div>

                <h3 style={{
                  fontSize: "1.3em",
                  fontWeight: "600",
                  marginBottom: "15px",
                  color: "white",
                  lineHeight: "1.3"
                }}>
                  {project.title}
                </h3>

                <p style={{
                  fontSize: "0.95em",
                  lineHeight: "1.5",
                  marginBottom: "20px",
                  color: "rgba(255, 255, 255, 0.8)"
                }}>
                  {project.description}
                </p>

                <div style={{ marginBottom: "20px" }}>
                  <h5 style={{
                    fontSize: "1em",
                    fontWeight: "600",
                    margin: "0 0 10px 0",
                    color: "#c770f0"
                  }}>
                    Key Features:
                  </h5>
                  <ul style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0
                  }}>
                    {project.keyFeatures.slice(0, 3).map((feature, featureIndex) => (
                      <li key={featureIndex} style={{
                        marginBottom: "8px",
                        paddingLeft: "20px",
                        position: "relative",
                        fontSize: "0.85em",
                        lineHeight: "1.4"
                      }}>
                        <span style={{
                          color: "#c770f0",
                          position: "absolute",
                          left: 0,
                          top: "2px"
                        }}>
                          ▸
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <h5 style={{
                    fontSize: "1em",
                    fontWeight: "600",
                    margin: "0 0 10px 0",
                    color: "#c770f0"
                  }}>
                    Technologies:
                  </h5>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px"
                  }}>
                    {project.technologies.slice(0, 4).map((tech, techIndex) => (
                      <span key={techIndex} style={{
                        backgroundColor: "rgba(199, 112, 240, 0.1)",
                        color: "#c770f0",
                        padding: "3px 8px",
                        borderRadius: "12px",
                        fontSize: "0.75em",
                        border: "1px solid rgba(199, 112, 240, 0.2)"
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{
                  backgroundColor: "rgba(56, 249, 215, 0.1)",
                  border: "1px solid rgba(56, 249, 215, 0.2)",
                  borderRadius: "10px",
                  padding: "12px",
                  marginTop: "15px"
                }}>
                  <h6 style={{
                    fontSize: "0.9em",
                    fontWeight: "600",
                    margin: "0 0 5px 0",
                    color: "#38f9d7"
                  }}>
                    Business Impact:
                  </h6>
                  <p style={{
                    fontSize: "0.8em",
                    margin: 0,
                    color: "rgba(56, 249, 215, 0.9)",
                    lineHeight: "1.4"
                  }}>
                    {project.impact}
                  </p>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </Container>
  );
}

export default FeaturedProjects;
